import { getValidAuthHeadersOrThrow } from '../lib/auth';

export type UnlinkedSkuReason =
  | 'not_in_shopify'
  | 'tracking_off'
  | 'duplicate_sku'
  | 'no_sku'
  | 'map_failed';

export interface UnlinkedSkuEntry {
  sku: string;
  product_id: string;
  reason: UnlinkedSkuReason;
}

export interface ShopifyLinkAllResult {
  success: boolean;
  updated_in_supabase: number;
  updated_by_shopify_id: number;
  prices_synced: number;
  cost_prices_synced: number;
  locations_synced: number;
  suppliers_synced: number;
  missing_in_supabase: string[];
  duplicates_in_supabase: Array<{ sku: string; product_ids: string[] }>;
  shopify_products_scanned: number;
  shopify_variants_processed: number;
  with_sku_count: number;
  without_sku_count: number;
  draft_skipped_count: number;
  linked: number;
  unlinked_skus: UnlinkedSkuEntry[];
  has_more?: boolean;
  next_shopify_cursor?: string | null;
  next_local_offset?: number | null;
  error?: string;
}

function mergeLinkAllResults(a: ShopifyLinkAllResult, b: ShopifyLinkAllResult): ShopifyLinkAllResult {
  const missing = [...new Set([...a.missing_in_supabase, ...b.missing_in_supabase])];
  const dupMap = new Map<string, Set<string>>();
  for (const d of [...a.duplicates_in_supabase, ...b.duplicates_in_supabase]) {
    const set = dupMap.get(d.sku) ?? new Set<string>();
    for (const id of d.product_ids) set.add(id);
    dupMap.set(d.sku, set);
  }
  const duplicates_in_supabase = [...dupMap.entries()].map(([sku, ids]) => ({
    sku,
    product_ids: [...ids],
  }));

  const unlinkedMap = new Map<string, UnlinkedSkuEntry>();
  for (const entry of [...a.unlinked_skus, ...b.unlinked_skus]) {
    unlinkedMap.set(entry.product_id, entry);
  }

  return {
    success: a.success && b.success,
    updated_in_supabase: a.updated_in_supabase + b.updated_in_supabase,
    updated_by_shopify_id: a.updated_by_shopify_id + b.updated_by_shopify_id,
    prices_synced: a.prices_synced + b.prices_synced,
    cost_prices_synced: a.cost_prices_synced + b.cost_prices_synced,
    locations_synced: a.locations_synced + b.locations_synced,
    suppliers_synced: a.suppliers_synced + b.suppliers_synced,
    missing_in_supabase: missing,
    duplicates_in_supabase,
    shopify_products_scanned: a.shopify_products_scanned + b.shopify_products_scanned,
    shopify_variants_processed: a.shopify_variants_processed + b.shopify_variants_processed,
    with_sku_count: a.with_sku_count + b.with_sku_count,
    without_sku_count: a.without_sku_count + b.without_sku_count,
    draft_skipped_count: a.draft_skipped_count + b.draft_skipped_count,
    linked: a.linked + b.linked,
    unlinked_skus: [...unlinkedMap.values()],
    has_more: b.has_more,
    next_local_offset: b.next_local_offset,
    next_shopify_cursor: b.next_shopify_cursor,
    error: b.error ?? a.error,
  };
}

interface RunShopifyLinkAllOptions {
  onProgress?: (part: number, partial: ShopifyLinkAllResult) => void;
}

export function formatUnlinkedReason(reason: UnlinkedSkuReason): string {
  switch (reason) {
    case 'not_in_shopify':
      return 'Not found in Shopify';
    case 'tracking_off':
      return 'Tracking off in Shopify';
    case 'duplicate_sku':
      return 'Duplicate SKU in app';
    case 'no_sku':
      return 'No valid SKU';
    case 'map_failed':
      return 'Mapping failed';
    default:
      return reason;
  }
}

export async function runShopifyLinkAll(
  options: RunShopifyLinkAllOptions = {}
): Promise<ShopifyLinkAllResult> {
  const MAX_CHUNK_RETRIES = 5;
  let merged: ShopifyLinkAllResult | null = null;
  let localOffset = 0;
  let chunkIndex = 0;
  let done = false;

  while (!done) {
    chunkIndex += 1;

    let chunkData: ShopifyLinkAllResult | undefined;
    for (let attempt = 1; attempt <= MAX_CHUNK_RETRIES; attempt++) {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-pull-products`,
        {
          method: 'POST',
          headers: await getValidAuthHeadersOrThrow(),
          body: JSON.stringify({
            link_only: true,
            local_offset: localOffset,
          }),
        }
      );

      let data: ShopifyLinkAllResult;
      try {
        data = (await response.json()) as ShopifyLinkAllResult;
        if (!Array.isArray(data.unlinked_skus)) {
          data.unlinked_skus = [];
        }
      } catch {
        if (response.status === 504) {
          throw new Error('Request timed out. Your store may have many products. Please try again.');
        }
        const isTransient =
          response.status === 502 ||
          response.status === 503 ||
          response.status === 546;
        if (isTransient && attempt < MAX_CHUNK_RETRIES) {
          await new Promise(r => setTimeout(r, 8000 * Math.pow(2, attempt - 1)));
          continue;
        }
        throw new Error(`Server returned ${response.status}. Please try again.`);
      }

      if (response.ok && data.success) {
        chunkData = data;
        break;
      }

      const errMsg = data.error || 'Failed to link products to Shopify';
      const isTransient =
        response.status === 502 ||
        response.status === 503 ||
        response.status === 546;
      if (isTransient && attempt < MAX_CHUNK_RETRIES) {
        await new Promise(r => setTimeout(r, 8000 * Math.pow(2, attempt - 1)));
        continue;
      }
      throw new Error(errMsg);
    }

    if (!chunkData) {
      throw new Error('Failed to link products to Shopify');
    }

    merged = merged ? mergeLinkAllResults(merged, chunkData) : chunkData;
    options.onProgress?.(chunkIndex, merged);

    if (!chunkData.has_more) {
      done = true;
      break;
    }

    const nextOffset = chunkData.next_local_offset;
    if (nextOffset == null) {
      throw new Error('Link incomplete: missing next batch offset. Try again.');
    }
    localOffset = nextOffset;
  }

  return merged!;
}
